import { useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import { APIResponse } from '../../../../../../lib/commons/api'
import { BulkSheetFilter, FilterFooter } from '../common'
import { Box, Checkbox, FormControlLabel, styled } from '@mui/material'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../../../store'
import usePrevious from '../../../../../hooks/usePrevious'
import { intl } from '../../../../../../i18n'

export type BoolFilter = {
  operator: BoolFilterOperator
}

export enum BoolFilterOperator {
  EXIST = 'EXIST',
  NOT_EXIST = 'NOT_EXIST',
  ALL_DESELECTED = 'ALL_DESELECTED',
}

type Props = CustomFilterProps & {
  labelExist?: string
  labelNotExist?: string
  fetch: (v: BoolFilter) => Promise<APIResponse>
}

/**
 * model から exists/notExists を導出する。
 * model === null: 両方チェック（フィルター非アクティブ）
 * model.operator === EXIST: exists=true, notExists=false
 * model.operator === NOT_EXIST: exists=false, notExists=true
 * model.operator === ALL_DESELECTED: exists=false, notExists=false
 */
const deriveExists = (model: BoolFilter | null): boolean =>
  !model || model.operator === BoolFilterOperator.EXIST

const deriveNotExists = (model: BoolFilter | null): boolean =>
  !model || model.operator === BoolFilterOperator.NOT_EXIST

const buildBoolFilterModel = (
  exists: boolean,
  notExists: boolean
): BoolFilter | null => {
  // 両方チェック = 非アクティブ
  if (exists && notExists) return null
  if (!exists && !notExists) {
    return { operator: BoolFilterOperator.ALL_DESELECTED }
  }
  if (exists) return { operator: BoolFilterOperator.EXIST }
  return { operator: BoolFilterOperator.NOT_EXIST }
}

export const ServerSideBoolFilter = ({
  model,
  onModelChange,
  api,
  labelExist,
  labelNotExist,
  fetch: fetchFn,
}: Props) => {
  const hasRequiredSaveData = useSelector<AllState>(
    state => state.hasRequiredSaveData.hasRequiredSaveData
  )
  const prevHasRequiredSaveData = usePrevious(hasRequiredSaveData)

  // Derive state from model (single source of truth)
  const exists = deriveExists(model as BoolFilter | null)
  const notExists = deriveNotExists(model as BoolFilter | null)
  const isAllDeselected = !exists && !notExists

  // UI-local state for async filtering
  const [loading, isLoading] = useState(false)
  const [filteredIds, setFilteredIds] = useState<Set<string> | undefined>(
    undefined
  )

  const addedRowIds = useRef<string[]>()
  useEffect(() => {
    if (addedRowIds.current) return
    addedRowIds.current = []
  }, [])

  const fetchFilteredIds = async (v: BoolFilter) => {
    const response = await fetchFn(v)
    setFilteredIds(new Set(response.json))
  }

  const search = useMemo(
    () =>
      _.debounce(async (v: BoolFilter) => {
        try {
          isLoading(true)
          await fetchFilteredIds(v)
        } finally {
          isLoading(false)
        }
      }, 300),
    []
  )

  // When criteria changes (model changes), trigger debounced search
  const modelKey = JSON.stringify(model)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- modelKey は model の値比較用。model 自体は毎レンダーで新規オブジェクトになるため deps に入れると毎回発火する。isAllDeselected は model から導出されるため modelKey で十分。search は useMemo で安定
  useEffect(() => {
    if (!model) {
      setFilteredIds(undefined)
      return
    }
    if (isAllDeselected) {
      setFilteredIds(new Set())
      return
    }
    search(model as BoolFilter)
  }, [modelKey])

  // When filteredIds changes, trigger re-filtering
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api は AG Grid から渡される安定参照。filteredIds 変更時のみ再フィルタリングをトリガーする
  useEffect(() => {
    api.onFilterChanged()
  }, [filteredIds])

  // Re-fetch after save
  // eslint-disable-next-line react-hooks/exhaustive-deps -- hasRequiredSaveData の変化のみをトリガーとし、その時点の最新 model/fetchFilteredIds を読む
  useEffect(() => {
    const isAfterSavedOrRefreshed =
      prevHasRequiredSaveData && !hasRequiredSaveData
    if (isAfterSavedOrRefreshed && model) {
      fetchFilteredIds(model as BoolFilter).then(() => {
        addedRowIds.current = []
      })
    }
  }, [hasRequiredSaveData])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.added) {
        if (addedRowIds.current && params.node.id) {
          addedRowIds.current.push(params.node.id)
        }
        return true
      }
      if (
        addedRowIds.current &&
        params.node.id &&
        addedRowIds.current.includes(params.node.id)
      ) {
        return true
      }
      if (!filteredIds) return true
      return params.node.id ? filteredIds.has(params.node.id) : false
    },

    getModelAsString() {
      if (!model) return ''
      if (labelExist || labelNotExist) {
        if (exists) return labelExist ?? ''
        if (notExists) return labelNotExist ?? ''
      }
      if (isAllDeselected) return '(Not Selected)'
      if (exists) return intl.formatMessage({ id: 'exist' })
      if (notExists) return intl.formatMessage({ id: 'not.exist' })
      return ''
    },
  })

  return (
    <BulkSheetFilter>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'start',
          flexDirection: 'column',
          margin: '0 3px',
        }}
      >
        <CheckboxFormControl
          label={labelExist}
          control={
            <Checkbox
              checked={exists}
              onChange={e =>
                onModelChange(buildBoolFilterModel(e.target.checked, notExists))
              }
              disableRipple={true}
            />
          }
        />
        <CheckboxFormControl
          label={labelNotExist}
          control={
            <Checkbox
              checked={notExists}
              onChange={e =>
                onModelChange(buildBoolFilterModel(exists, e.target.checked))
              }
              disableRipple={true}
            />
          }
        />
      </Box>
      <FilterFooter loading={loading} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}

const CheckboxFormControl = styled(FormControlLabel)({
  margin: 0,
})
